'use client'

import posthog from 'posthog-js'
import { useEffect } from 'react'

// One auto-reload attempt is allowed per pathname per this window. A fresh
// error outside the window (a stable page that later breaks) reloads again;
// two crashes back-to-back inside the window fall through to the fallback UI
// instead of looping. 30s is long enough for a stale-chunk reload to settle
// and short enough that a real regression 5 minutes later still gets a retry.
const RELOAD_COOLDOWN_MS = 30_000

export default function Error({ error, reset }: { error: Error & { digest?: string }; reset: () => void }) {
  useEffect(() => {
    const reloadKey = 'error_reload_at_' + window.location.pathname
    const lastReloadAt = Number(sessionStorage.getItem(reloadKey) ?? '0')
    const reloadedRecently = lastReloadAt > 0 && Date.now() - lastReloadAt < RELOAD_COOLDOWN_MS

    // Report every boundary hit — even the ones we're about to auto-reload
    // through — so we can tell "transient error the user never saw" apart from
    // "user is staring at Try again right now". The old code only captured the
    // second hit, which hid the actual first-hit cause and made the population
    // that ends up on the fallback UI unmeasurable.
    posthog.captureException(error, {
      source: 'error_boundary',
      reloadedBefore: reloadedRecently,
      message: error.message,
      stack: error.stack,
      digest: error.digest,
      url: window.location.href,
    })

    if (!reloadedRecently) {
      sessionStorage.setItem(reloadKey, String(Date.now()))
      // Preserve the existing query string. Stripping it turned a recoverable
      // stale-chunk error into a second-error on any page that needs its
      // params — the shared review page (`?pwd=`), the post-checkout return
      // (`?checkout_session_id=`), the password-reset link (`?token=`) — and
      // that second error is what surfaces Try again to the user.
      const url = new URL(window.location.href)
      url.searchParams.set('cache', String(Date.now()))
      window.location.replace(url.toString())
    }
  }, [error])

  return (
    <div className="flex min-h-screen items-center justify-center p-4">
      <div className="w-full max-w-md space-y-4 text-center">
        <h2 className="text-2xl font-bold">Something went wrong</h2>
        <p className="text-muted-foreground">An error occurred while loading this page. Please try again.</p>
        <button onClick={reset} className="rounded-md bg-primary px-4 py-2 text-primary-foreground hover:bg-primary/90">
          Try again
        </button>
      </div>
    </div>
  )
}
